/**
 * Logo component — three variants:
 *
 *  "icon"    Coloured gradient square + white figure.
 *            Use on white/light backgrounds (landing page, docs, auth modal).
 *
 *  "header"  White figure only, no background.
 *            Use on the coloured app header where the background IS the brand colour.
 *
 *  "full"    Icon + wordmark side by side.
 *            Use in the sidebar brand and landing page nav.
 *
 * The icon shape: an oval body with an arc of five decreasing circles,
 * matching the Oliver Design System export (2026-04-19).
 * All geometry is on a 100×100 viewBox so it scales cleanly at any size.
 */
const Figure = ({ fill = "white" }) => (
  <g fill={fill}>
    {/* Body */}
    <ellipse cx="42" cy="55" rx="18" ry="22" />
    {/* Arc of circles — decreasing radius, top-right */}
    <circle cx="62" cy="36" r="6" />
    <circle cx="70" cy="44" r="5" />
    <circle cx="74" cy="54" r="4.5" />
    <circle cx="74" cy="63" r="4" />
    <circle cx="72" cy="71" r="3.5" />
  </g>
);

export default function Logo({ variant = "icon", size = 40, className = "" }) {

  if (variant === "header") {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 100"
        width={size}
        height={size}
        aria-label="Baby Tracker logo"
        role="img"
        className={className}
        style={{ display: "block", flexShrink: 0 }}
      >
        <Figure fill="white" />
      </svg>
    );
  }

  if (variant === "full") {
    const iconSize = size;
    return (
      <span
        className={className}
        style={{ display: "inline-flex", alignItems: "center", gap: `${Math.round(iconSize * 0.3)}px` }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          width={iconSize}
          height={iconSize}
          aria-hidden="true"
          style={{ display: "block", flexShrink: 0 }}
        >
          <defs>
            <linearGradient id="logoGradFull" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FFB3D9" />
              <stop offset="100%" stopColor="#C2185B" />
            </linearGradient>
          </defs>
          <rect width="100" height="100" rx="22" ry="22" fill="url(#logoGradFull)" />
          <Figure />
        </svg>
        <span style={{
          fontWeight: 800,
          fontSize: `${Math.round(iconSize * 0.45)}px`,
          letterSpacing: "-0.3px",
          lineHeight: 1,
        }}>
          Baby Tracker
        </span>
      </span>
    );
  }

  // Default: "icon" — full coloured rounded square + white figure
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      aria-label="Baby Tracker logo"
      role="img"
      className={className}
      style={{ display: "block", flexShrink: 0 }}
    >
      <defs>
        <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFB3D9" />
          <stop offset="100%" stopColor="#C2185B" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="22" ry="22" fill="url(#logoGrad)" />
      <Figure />
    </svg>
  );
}
